import { Client } from "@bugsnag/browser";
import { LocationProps } from "@types";
import configActions from "actions/configs";
import mapActions from "actions/map";
import profileActions from "actions/profile";
import { OrganizationProfileModel } from "api";
import AssistantChatReport from "components/assistantChatReport";
import AtiSuOrganizationRegistration from "components/auth/atiSuOrganizationRegistration";
import ConnectCars from "components/connectCars";
import ForwardingAuth from "components/forwarding/auth";
import ForwardingSuitableOrder from "components/forwarding/suitableOrder";
import Monitoring from "components/monitoring/container";
import OrganizationRatings from "components/organizationRatings";
import ErrorBoundary from "components/shared/errorBoundary";
import Layout from "components/shared/layout";
import { ProvideScrollPosition } from "contexts/scrollPosition";
import { organizationDemoPeriodStatusesEnum, organizationRegistrationStatusesEnum } from "helpers/enums";
import { RegionDryingPeriod } from "helpers/models";
import { paths, RouteObj, StoredRouteModels } from "helpers/paths";
import { generatedActions } from "helpers/redux";
import { RolesModel } from "helpers/roles";
import { ymEvent } from "helpers/system";
import { UmamiContainer, UmamiManager } from "lib/umami";
import policy from "policies";
import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { matchPath, Route, useHistory } from "react-router-dom";
import { Action, ActionFunctionAny } from "redux-actions";
import { startDemoPeriod } from "sources/profile/organization";
import { shouldStartOrganizationDemoPeriod } from "./registration";

interface AppContainerModel {
    initialProfileLoaded: boolean;
    organizationLoaded: boolean;
    routes: StoredRouteModels;
    bugsnagClient?: Client;
    canEditBooks?: (book: string) => boolean;
    canCreateBooks?: (book: string) => boolean;
    roles: RolesModel;
    clearParams: () => void;
    getOrg: () => void;
    profile: any;
    resetPaginationPage: () => void;
    organization: OrganizationProfileModel;
}

const AppContainer = (props: AppContainerModel & { location: LocationProps }) => {
    const { routes, bugsnagClient, ...rest } = props;
    const {
        canEditBooks,
        canCreateBooks,
        initialProfileLoaded,
        roles,
        clearParams,
        profile,
        resetPaginationPage,
        organization,
        organizationLoaded,
    } = rest;
    const history = useHistory();
    const shouldStartDemoPeriod = shouldStartOrganizationDemoPeriod(organization);

    useEffect(() => {
        if (!initialProfileLoaded || !organizationLoaded) {
            return;
        }

        if (shouldStartDemoPeriod) {
            ymEvent("DemoPeriodStarted");
            UmamiManager.track("DemoPeriodStarted");
            startDemoPeriod()
                .then((res) => {
                    if (res) {
                        props.getOrg();
                        return history.push(paths.root);
                    }
                })
                .catch(() => {
                    alert({
                        type: "error",
                        message: "Не удалось запустить пробный период.",
                        title: "Ошибка",
                    });
                });
            return;
        }

        switch (organization.registrationStatus) {
            case organizationRegistrationStatusesEnum.OrganizationCreated:
            case organizationRegistrationStatusesEnum.RelayCreated:
                return history.push(paths.registrationCreateRelay);
            case organizationRegistrationStatusesEnum.RelayVerified:
                return history.push(paths.registrationAddCars);
            case organizationRegistrationStatusesEnum.RelaySetupCompleted:
                return history.push(paths.registrationAddDrivers);
            case organizationRegistrationStatusesEnum.DriversAdded:
                return history.push(paths.registrationAddTrucks);
        }
    }, [initialProfileLoaded, organization.registrationStatus, organizationLoaded, shouldStartDemoPeriod]);

    useEffect(() => {
        if (bugsnagClient && profile && profile.id) {
            bugsnagClient.setUser(String(profile.id), profile.email, profile.fullName);
            bugsnagClient.addMetadata("user", {
                name: profile.fullName,
                email: profile.email,
                id: profile.id,
                phoneNumber: profile.phoneNumber,
                roles: profile.roles,
                availableFeatureModules: profile.availableFeatureModules,
                canCreateBids: profile.canCreateBids,
                canEditBooks,
            });
            bugsnagClient.addMetadata("organization", {
                id: profile.organizationId,
                name: profile.organizationName,
            });
        }
    }, [profile]);

    const renderItem = (item: RouteObj, i: number) => {
        const Comp = item.Component as React.ComponentType<{
            canEditBooks?: (book: string) => boolean;
            canCreateBooks?: (book: string) => boolean;
            roles: RolesModel;
            clearParams: () => void;
            resetPaginationPage: () => void;
        }>;
        return (
            <Route
                key={i}
                path={item.path as string}
                render={(props: {}) => (
                    <ErrorBoundary key={item.path as string} className="page-content page-col page-col--full mounted">
                        <ProvideScrollPosition>
                            <Comp
                                {...props}
                                canEditBooks={canEditBooks}
                                canCreateBooks={canCreateBooks}
                                roles={roles}
                                clearParams={clearParams}
                                resetPaginationPage={resetPaginationPage}
                            />
                        </ProvideScrollPosition>
                    </ErrorBoundary>
                )}
            />
        );
    };

    const pathPolicy = useMemo(() => {
        return policy("path", {
            user: { roles },
            record: {},
            options: { availableFeatureModules: rest.profile.availableFeatureModules },
        });
    }, []);

    const componentsByMatch = () => {
        if (rest.location.pathname === paths.registrationAtiSu) {
            return <AtiSuOrganizationRegistration profile={profile} />;
        }

        const assistantChatReportMatch = matchPath(rest.location.pathname, {
            path: paths.assistantChatReport(":reportType", ":messageId"),
        });
        if (assistantChatReportMatch) {
            return <AssistantChatReport {...props} match={assistantChatReportMatch} />;
        }
        const organizationRatingsMatch = matchPath(rest.location.pathname, { path: paths.organizationRatings });
        if (organizationRatingsMatch) {
            return <OrganizationRatings {...props} />;
        }
        const registrationMatch = matchPath(rest.location.pathname, { path: paths.registration });
        if (registrationMatch) {
            return <ConnectCars {...props} />;
        }

        const isDemoExpired = organization?.demoPeriod?.status === organizationDemoPeriodStatusesEnum.Expired;

        let shouldBlockPage = false;
        const isChiefAdmin = profile.id === organization.adminId;

        if (isDemoExpired && !isChiefAdmin) {
            const currentPath = rest.location.pathname;
            const isAllowedPath =
                currentPath === paths.root ||
                currentPath.startsWith(paths.profile) ||
                currentPath.startsWith(paths.bid) ||
                currentPath.startsWith(paths.reports) ||
                currentPath.startsWith(paths.finances);

            if (!isAllowedPath) {
                shouldBlockPage = true;
            }
        }

        return (
            <>
                {pathPolicy.canSeeForwarding() && (
                    <>
                        <Route exact path={paths.forwardingAuth} component={ForwardingAuth} />
                        <Route exact path={paths.forwardingSuitableOrder} component={ForwardingSuitableOrder} />
                    </>
                )}
                <Monitoring />
                <Layout {...rest}>
                    {shouldBlockPage ? (
                        <div
                            className="d-flex align-items-center justify-content-center h-100 w-100 flex-column"
                            style={{ minHeight: "100vh", width: "100%" }}
                        >
                            <h2 className="text-secondary font-weight-bold mb-3">Демо-период истек</h2>
                            <p className="text-muted font-size-18">Обратитесь к администратору организации</p>
                        </div>
                    ) : (
                        [
                            routes.map((item, i) =>
                                item.items ? item.items.map(renderItem) : renderItem(item as RouteObj, i),
                            ),
                        ]
                    )}
                </Layout>
            </>
        );
    };

    return (
        <UmamiContainer
            profile={props.profile}
            organization={props.organization}
            initialProfileLoaded={props.initialProfileLoaded}
        >
            {componentsByMatch()}
        </UmamiContainer>
    );
};

const mapStateToProps = (state: any) => ({
    token: state.profile.token,
    initialProfileLoaded: state.profile.initialProfileLoaded,
    organizationLoaded: state.profile.organizationLoaded,
    isLoading: state.dictionaries.isInitialLoading,
    profile: state.profile.profile,
    enums: state.dictionaries.enums,
    organization: state.profile.organization,
    roles: state.profile.roles,
    fullscreen: state.layout.fullscreen,
    routes: state.layout.routes,
    isMapAboveContent: state.map.mode === "picker",
    canEditBooks: (book: string) =>
        policy("user", {
            user: {
                canEditAll: !state.profile.organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: state.profile.organization.intBooksEditPermissionsFlags,
                roles: state.profile.roles,
                isDemoPeriodExpired: state.profile.profile?.isDemoPeriodExpired,
            },
            record: {},
            options: { book },
        }).canEditBook(),
    canCreateBooks: (book: string) =>
        policy("user", {
            user: {
                canEditAll: !state.profile.organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: state.profile.organization.intBooksEditPermissionsFlags,
                roles: state.profile.roles,
                isDemoPeriodExpired: state.profile.profile?.isDemoPeriodExpired,
            },
            record: {},
            options: { book },
        }).canCreateBook(),
});

const mapDispatchToProps = (dispatch: ActionFunctionAny<Action<Object>>) => ({
    getInitial: () => dispatch(profileActions.getInitial()),
    getOrg: () => dispatch(profileActions.getOrg()),
    mergeMapData: (params: {}) => dispatch(mapActions.mergeMapData(params)),
    switchMap: (params: {}) => dispatch(mapActions.switchMap(params)),
    getDictionaries: () => dispatch(generatedActions.dictionaries.index()),
    loadConfigs: () => dispatch(configActions.loadConfigs()),
    getPlatonPoints: () => dispatch(mapActions.getPlatonPoints()),
    getPostamatPoints: () => dispatch(mapActions.getPostamatPoints()),
    getPaymentPoints: () => dispatch(mapActions.getPaymentPoints()),
    getRoadRestrictions: () => dispatch(mapActions.getRoadRestrictions()),
    getRoadRestrictionTypes: () => dispatch(mapActions.getRoadRestrictionTypes()),
    setRegionDryingPeriods: (data: RegionDryingPeriod[]) => dispatch(mapActions.setRegionDryingPeriods(data)),
    clearParams: (key: string, params = {}) =>
        dispatch(
            generatedActions[key].merge({ filters: [], start: 0, customFilters: {}, preparedFilter: {}, ...params }),
        ),
    resetPaginationPage: (key: string) => dispatch(generatedActions[key].merge({ start: 0 })),
});

export default connect<any, any, any>(mapStateToProps, mapDispatchToProps)(AppContainer as any);
